/**
 * 606 Meta5 AI Trading - History Tab
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { History, ChevronDown, ChevronUp, RefreshCw, Loader2 } from 'lucide-react';
import { useTrading } from '../../lib/trading-i18n';
import { fetchPositionsHistory } from '../../lib/trading-api';
import PnL from '../../components/trading/PnL';
import Badge from '../../components/trading/Badge';

const HistoryTab = ({ language = 'ar' }) => {
  const t = useTrading(language);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const loadHistory = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const data = await fetchPositionsHistory(100);
      setHistory(data.data || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadHistory({ silent: true });
    // Auto-refresh every 15s so closed trades show without a manual reload
    const interval = setInterval(() => loadHistory({ silent: true }), 15000);
    return () => clearInterval(interval);
  }, [loadHistory]);

  const formatTime = (isoString) => {
    try {
      return new Date(isoString).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '--';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-4"
    >
      <div className="flex items-center justify-between mb-4 gap-3">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <History className="h-5 w-5 text-[#d4af37]" />
          {t.history}
          <span className="text-xs text-[#71717A] font-normal">({history.length})</span>
        </h3>
        <button
          type="button"
          onClick={() => loadHistory()}
          disabled={refreshing}
          className="p-2 rounded-lg bg-white/5 border border-white/10 text-[#71717A] hover:text-white hover:border-white/20 transition-colors disabled:opacity-50"
          data-testid="history-refresh-btn"
        >
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
      </div>
      
      {history.length === 0 ? (
        <p className="text-[#71717A] text-center py-12">{t.noHistory}</p>
      ) : (
        <div className="space-y-2">
          {history.map((pos, idx) => (
            <div key={`${pos.ticket}-${idx}`} className="bg-white/[0.02] rounded-lg border border-white/5">
              <div
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/[0.02]"
                onClick={() => setExpandedRow(expandedRow === pos.ticket ? null : pos.ticket)}
              >
                <div className="flex items-center gap-4">
                  <Badge variant={pos.type}>{pos.type === 'buy' ? t.buy : t.sell}</Badge>
                  <span className="text-white font-medium">{pos.symbol}</span>
                  <span className="text-[#71717A] text-sm">{pos.volume} lots</span>
                  <Badge variant={pos.closed_by === 'TP' ? 'success' : pos.closed_by === 'SL' ? 'danger' : 'default'}>
                    {pos.closed_by}
                  </Badge>
                </div>
                <div className="flex items-center gap-4">
                  <PnL value={pos.profit || 0} />
                  <span className="text-[#71717A] text-xs">{formatTime(pos.close_time)}</span>
                  {expandedRow === pos.ticket ? (
                    <ChevronUp className="h-4 w-4 text-[#71717A]" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-[#71717A]" />
                  )}
                </div>
              </div>
              
              {expandedRow === pos.ticket && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="border-t border-white/5 p-3 bg-white/[0.01]"
                >
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-[#71717A]">{t.openPrice}:</span>
                      <span className="text-white ml-2 font-mono">{pos.open_price?.toFixed(5)}</span>
                    </div>
                    <div>
                      <span className="text-[#71717A]">{t.closePrice}:</span>
                      <span className="text-white ml-2 font-mono">{pos.close_price?.toFixed(5)}</span>
                    </div>
                    <div>
                      <span className="text-[#71717A]">{t.stopLoss}:</span>
                      <span className="text-red-400 ml-2 font-mono">{pos.sl?.toFixed(5) || '--'}</span>
                    </div>
                    <div>
                      <span className="text-[#71717A]">{t.takeProfit}:</span>
                      <span className="text-emerald-400 ml-2 font-mono">{pos.tp?.toFixed(5) || '--'}</span>
                    </div>
                  </div>
                  {pos.ai_reason && (
                    <div className="mt-3 p-2 bg-[#d4af37]/10 rounded text-sm">
                      <span className="text-[#d4af37]">AI Reason:</span>
                      <span className="text-white/80 ml-2">{pos.ai_reason}</span>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default HistoryTab;
